import {
  IonicPage,
  NavController,
  ModalController,
  ToastController,
} from "ionic-angular";
import { Component } from "@angular/core";

import { HttpService } from "./../../providers/http-service";
import { PermissionService } from "./../../providers/permission-service";

import { ContratoFormPage } from "./contrato-form/contrato-form";
import { ContratoSearchPage } from "./contrato-search/contrato-search";

import { Contrato } from "./../../domain/contrato";

@IonicPage()
@Component({
  selector: "contratos-home",
  templateUrl: "contratos.html",
})
export class ContratosPage {
  public contrato: Contrato;
  public loaded: boolean = false;

  public pages: Array<{ title: string; component: any; icon: string; visible?: boolean; disabled?: boolean }>;

  constructor(
    public navCtrl: NavController,
    public modalCtrl: ModalController,
    public toastCtrl: ToastController,
    public http: HttpService,
    private permissionService: PermissionService
  ) {
    // Initialize pages array to prevent undefined errors
    this.pages = [];
  }

  ionViewDidLoad() {
    // Implementar controle de permissões - Ticket #5954
    this.checkPageAccess();
  }

  /**
   * Verifica acesso geral à página e configura interface
   * Ticket #5954 - Evita loops de erro
   */
  private checkPageAccess(): void {
    try {
      const hasBasicAccess = this.permissionService.hasPermission('contratos', 'index');
      const canCreate = this.permissionService.canCreate('contratos');
      const userProfile = this.permissionService.getCurrentUserProfile();
      
      const profileName = userProfile && userProfile.nome ? userProfile.nome : 'sem perfil';
      console.info('[CONTRATOS] Perfil:', profileName, ', Acesso básico:', hasBasicAccess, ', Pode cadastrar:', canCreate);
      
      // ADMINISTRADOR e OPERADOR sempre têm acesso
      if (userProfile && (userProfile.isAdmin || userProfile.isOperador)) {
        console.info('[CONTRATOS] Acesso total concedido por perfil privilegiado:', profileName);
        this.setupPageInterface(true, userProfile); // Acesso total
        return;
      }
      
      // Se não tem acesso básico, redirecionar
      if (!hasBasicAccess) {
        console.info('[CONTRATOS] Acesso negado para perfil:', profileName);
        this.permissionService.logPermissionCheck('contratos', 'index', false);
        
        let toast = this.toastCtrl.create({
          message: 'Você não tem permissão para acessar contratos',
          duration: 3000,
          position: 'top',
          cssClass: 'toast-error'
        });
        toast.present();
        
        // Voltar para home
        this.navCtrl.setRoot('HomePage');
        return;
      }

      // Configurar interface baseada em permissões
      this.setupPageInterface(canCreate, userProfile);
    } catch (error) {
      console.info('[CONTRATOS] Erro ao verificar acesso:', error, '- Configurando interface padrão');
      // Em caso de erro, configurar interface básica
      this.setupPageInterface(false, null);
    }
  }

  /**
   * Configura interface da página baseada em permissões
   */
  private setupPageInterface(canCreate: boolean, userProfile: any): void {
    try {
      this.pages = [
        { 
          title: "Novo", 
          component: ContratoFormPage, 
          icon: "person-add",
          visible: canCreate,
          disabled: !canCreate
        },
        { 
          title: "Buscar", 
          component: ContratoSearchPage, 
          icon: "search",
          visible: true, // Busca sempre permitida para quem tem acesso básico
          disabled: false
        }
      ];
    } catch (error) {
      console.info('[CONTRATOS] Erro ao configurar interface:', error);
      // Fallback para interface básica
      this.pages = [
        { 
          title: "Buscar", 
          component: ContratoSearchPage, 
          icon: "search",
          visible: true,
          disabled: false
        }
      ];
    }

    // Log de auditoria para ações críticas
    if (!canCreate && userProfile && userProfile.isOperadorReciclagem) {
      this.permissionService.logPermissionCheck('contratos', 'cadastrar', false);
      console.info('[AUDIT] OPERADOR RECICLAGEM bloqueado de cadastrar contratos - Ticket #5954');
    }

    // Se só tem acesso de leitura, mostrar aviso
    if (!canCreate && userProfile && userProfile.isOperadorReciclagem) {
      console.info('[INFO] OPERADOR RECICLAGEM tem apenas acesso de consulta a contratos');
    }
  }

  navegarPara(page) {
    // Verificar permissão antes de navegar para páginas de cadastro
    if (page.component === ContratoFormPage) {
      const canCreate = this.permissionService.canCreate('contratos');
      
      if (!canCreate) {
        this.permissionService.logPermissionCheck('contratos', 'cadastrar', false);
        
        let toast = this.toastCtrl.create({
          message: 'Você não tem permissão para cadastrar contratos',
          duration: 3000,
          position: 'top',
          cssClass: 'toast-error'
        });
        toast.present();
        return;
      }
    }

    this.navCtrl.push(page.component, {});
  }

  /**
   * Getter para filtrar páginas visíveis
   * Defensive programming para evitar erro quando pages ainda não foi inicializado
   */
  get visiblePages() {
    try {
      if (!this.pages || !Array.isArray(this.pages)) {
        return [];
      }
      return this.pages.filter(page => page && page.visible !== false);
    } catch (error) {
      console.info('[CONTRATOS] Erro ao filtrar páginas visíveis:', error);
      return [];
    }
  }

  /**
   * Verifica se uma página está desabilitada
   */
  isPageDisabled(page: any): boolean {
    try {
      return page && page.disabled === true;
    } catch (error) {
      console.info('[CONTRATOS] Erro ao verificar se página está desabilitada:', error);
      return false;
    }
  }

  /**
   * Helper para verificar se é OPERADOR RECICLAGEM
   */
  isOperadorReciclagem(): boolean {
    return this.permissionService.isCurrentUserOperadorReciclagem();
  }

  /**
   * Helper para obter nome do perfil atual
   */
  getCurrentProfileName(): string {
    return this.permissionService.getCurrentUserProfileName();
  }
}



// WEBPACK FOOTER //
// ./src/pages/contratos/contratos.ts